import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { 
  History, CheckCircle2, XCircle, Clock, Award, 
  Calendar, GraduationCap, FileText
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PortalHistorial = () => {
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistorial();
  }, []);

  const fetchHistorial = async () => {
    try {
      const res = await axios.get(`${API}/trabajador/historial`);
      setHistorial(res.data);
    } catch (error) {
      toast.error('Error al cargar historial');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  const aprobados = historial.filter(h => h.aprobado);
  const reprobados = historial.filter(h => !h.aprobado && h.fecha_completado);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Mi Historial</h1>
        <p className="text-slate-500">Registro de tus capacitaciones completadas</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <History className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{historial.length}</p>
              <p className="text-sm text-slate-500">Total Evaluaciones</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
              <Award className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{aprobados.length}</p>
              <p className="text-sm text-slate-500">Aprobadas</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
              <XCircle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{reprobados.length}</p>
              <p className="text-sm text-slate-500">No Aprobadas</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Historial List */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-teal-500" />
            Historial de Evaluaciones
          </CardTitle>
        </CardHeader>
        <CardContent>
          {historial.length === 0 ? (
            <div className="text-center py-12">
              <History className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No tienes evaluaciones registradas</p>
            </div>
          ) : (
            <div className="space-y-3">
              {historial.map((item) => (
                <div 
                  key={item.id}
                  className={`flex items-center gap-4 p-4 rounded-lg border ${
                    item.aprobado 
                      ? 'bg-emerald-50 border-emerald-200' 
                      : 'bg-red-50 border-red-200'
                  }`}
                  data-testid={`historial-item-${item.id}`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    item.aprobado ? 'bg-emerald-100' : 'bg-red-100'
                  }`}>
                    {item.aprobado ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600" />
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900">{item.capacitacion_nombre}</p>
                    <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-slate-500">
                      {item.fecha_completado && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(item.fecha_completado).toLocaleDateString('es-CL')}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <FileText className="w-3 h-3" />
                        Intento #{item.intentos}
                      </span>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <p className={`text-2xl font-bold ${
                      item.aprobado ? 'text-emerald-600' : 'text-red-600'
                    }`}>
                      {item.nota}%
                    </p>
                    <p className={`text-xs ${
                      item.aprobado ? 'text-emerald-600' : 'text-red-600'
                    }`}>
                      {item.aprobado ? 'Aprobado' : 'No Aprobado'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
